import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BeakerIcon, PlusIcon } from '@heroicons/react/24/outline';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import Tabs from '../../components/Tabs';
import Modal from '../../components/Shell/Modal';
import {
	TemplateBoolean,
	TemplateField,
} from '../../components/Attributes/Attributes_old';
import Button from '../../components/Button';
import { Label } from '../../components/Form/tailwindForm';
import DataTable from '../../components/DataTable';
import { Spinner } from '../../components/Form';
import BetaAttributes from '../../components/Attributes';
import Badge from '../../components/Badge';

import useAsync from '../../hooks/useAsync';
import useRequired from '../../hooks/useRequired';
import useModal from '../../hooks/useModal';

import {
	addConsumableToStoragePointShelf,
	addShelfStoragePoint,
	deleteStoragePoint,
	getStoragePoint,
	getStoragePointConsumables,
	updateShelfStoragePoint,
} from '../../api/storage-points';
import { getConsumables } from '../../api/consumables';

const stockLabels = {
	out: {
		label: 'Out of Stock',
		badge: 'error',
	},
	restock: {
		label: 'Need to restock',
		badge: 'warning',
	},
	instock: {
		label: 'In stock',
		badge: 'success',
	},
};

const CONSUMABLES_MODULE_ID = process.env.REACT_APP_MODULE_ID_CONSUMABLES;

const getStock = ({ quantity, min, max }) => {
	let stock = 'instock';

	if (quantity === 0) {
		stock = 'out';
	} else if (min && quantity < min) {
		stock = 'restock';
	}

	const label =
		stock === 'restock'
			? `${stockLabels[stock]?.label} (min. ${min})`
			: stockLabels[stock]?.label;

	return {
		badge: (
			<Badge
				isBeta
				status={stockLabels[stock]?.badge}
				size="xxs"
				ghost={stock === 'instock'}
			>
				{label}
			</Badge>
		),
		label,
	};
};

const StoragePoint = () => {
	const {
		open: isDeleteModalOpen,
		setOpen: openDeleteModal,
		setClose: closeDeleteModal,
	} = useModal('deleteModal');

	const navigate = useNavigate();
	const { id } = useParams();
	const [isAddConsumableOpen, setAddConsumableOpen] = useState(null);
	const [configuration, setConfiguration] = useState(null);
	const [editingTitle, setEditingTitle] = useState(null);
	const [resetInventory, setResetInventory] = useState(true);
	const [activeTab, setActiveTab] = useState(null);

	const [addConsumableForm, setAddConsumableForm] = useState({});

	const [shallowStoragePoint, setShallowStoragePoint] = useState({});

	const {
		value: storagePoint,
		execute: onRefreshStoragePoint,
		status: isLoadingStoragePoint,
	} = useAsync(getStoragePoint, {
		defaultPayload: { id },
	});

	const {
		value: consumables,
		status: isLoadingConsumables,
		execute: refreshConsumables,
	} = useAsync(getConsumables, { immediate: false });

	const {
		value: storagePointConsumables,
		status: isLoadingStorageConsumbles,
		execute: refreshStorageConsumables,
	} = useAsync(getStoragePointConsumables, {
		defaultPayload: { id },
	});

	const { execute: onDeleteStoragePoint } = useAsync(deleteStoragePoint, {
		immediate: false,
	});

	const { execute: onAddConsumable, status: isAddingConsumable } = useAsync(
		addConsumableToStoragePointShelf,
		{ immediate: false }
	);

	const { execute: onUpdateShelf, status: isUpdatingShelf } = useAsync(
		updateShelfStoragePoint,
		{ immediate: false }
	);

	const { execute: onAddShelf, status: isAddingShelf } = useAsync(
		addShelfStoragePoint,
		{
			immediate: false,
			defaultPayload: { id },
		}
	);

	/*useEffect(() => {
		if (isAddConsumableOpen) {
			refreshConsumables({ pageSize: null });
		} else {
			setSelectedConsumable('');
			setAddConsumableForm({});
		}
	}, [isAddConsumableOpen]);*/

	useEffect(() => {
		if (!isDeleteModalOpen) {
			setResetInventory(true);
		}
	}, [isDeleteModalOpen]);

	useEffect(() => {
		if (!isAddConsumableOpen) {
			setAddConsumableForm({});
			refreshStorageConsumables();
		}
	}, [isAddConsumableOpen]);

	useEffect(() => {
		if (storagePoint) {
			setConfiguration([
				...(storagePoint?.shelves?.length
					? storagePoint?.shelves?.map((shelf) => ({
							name: shelf?.name,
							rows: shelf?.inventories || [],
					  }))
					: [{ name: null, rows: [] }]),
			]);

			setShallowStoragePoint(storagePoint);
		}
	}, [storagePoint]);

	const handleShiftShelf = (index, direction) => {
		const updatedConfig = [...configuration];
		const element = updatedConfig[index];
		updatedConfig.splice(index, 1);
		updatedConfig.splice(
			direction === 'up' ? index - 1 : index + 1,
			0,
			element
		);

		setConfiguration(updatedConfig);
	};

	const renderCard = useCallback((card, index, shelfIdx) => {
		const image = card?.consumable?.image;

		return (
			<div className={'rounded-xl p-6 group'}>
				<div className="flex flex-1 flex-col">
					{image ? (
						<img
							className="mt-2 mx-auto h-40 flex-shrink-0 rounded-xl"
							src={image}
							alt=""
						/>
					) : (
						<div className="mt-2 h-40 border border-gray-200 rounded-xl flex items-center justify-center">
							<BeakerIcon className="w-12 h-12 text-gray-200" />
						</div>
					)}
					<h3 className="mt-6 text-xs font-medium text-gray-900 text-center truncate">
						{card?.consumable?.name}
					</h3>

					{card?.minimum ? (
						<div className="text-center text-xxs text-gray-500 font-light mt-1.5">
							{`Reorder: ${card?.minimum}`}
						</div>
					) : null}

					{card?.maximum ? (
						<div className="text-center text-xxs text-gray-500 font-light mt-1.5">
							{`Max: ${card?.maximum}`}
						</div>
					) : null}

					{/*}
					<div className="invisible mt-3 flex items-center justify-center group-hover:visible">
						<ArrowLeftCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer mr-1" />
						<ArrowRightCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer ml-1" />
					</div>*/}
				</div>
			</div>
		);
	}, []);

	const handleTitleUpdate = (value, shelfId) => {
		onUpdateShelf({ id, name: value, shelfId }, () => {
			onRefreshStoragePoint({ silent: true });
			setEditingTitle(null);
		});
	};

	return (
		<MainContent maxWidth="max-w-7xl">
			{isLoadingStoragePoint === 'pending' ? (
				<LoadingSkeleton />
			) : (
				<>
					<PageHeading
						onBack={() => navigate(`/consumables/storage-points`)}
					>{`Storage Point: ${storagePoint?.name}`}</PageHeading>

					<Tabs
						className="-mt-4"
						tabs={[
							{
								name: 'Inventory',
								key: 'consumables',
								onClick: (key) => setActiveTab(key),
							},
							{
								name: 'Configure',
								key: 'configure',
								onClick: (key) => setActiveTab(key),
							},
							{
								name: 'Labels',
								key: 'labels',
								onClick: (key) => setActiveTab(key),
							},
							{
								name: 'Settings',
								key: 'settings',
								onClick: (key) => setActiveTab(key),
							},
						]}
						activeTab={activeTab}
					>
						{(tab) => {
							if (tab === 'consumables') {
								return (
									<DataTable
										actions={[{ label: 'Create Picklist' }]}
										columns={[
											{
												header: 'NAME',
												accessorKey: 'consumable.name',
												cell: (info) => {
													return info?.getValue() || null;
												},
											},
											{
												header: 'LOCATION',
												accessorKey: 'shelf.name',
												cell: (info) => {
													const order =
														info?.row?.original?.shelf?.storage_point?.shelfOrder?.indexOf(
															info?.getValue()?.id
														);
													return info?.getValue() || `Shelf ${order + 1}`;
												},
											},
											{
												header: 'QUANTITY',
												accessorKey: 'quantity',
												cell: (info) => {
													const trackInventory =
														info?.row?.original?.trackInventory;
													const quantity = info?.row?.original?.quantity;
													const min = info?.row?.original?.minimum;
													const max = info?.row?.original?.maximum;

													return trackInventory ? (
														<div className="">{quantity}</div>
													) : (
														'-'
													);
												},
											},
											{
												header: 'STATUS',
												accessorKey: 'quantity',
												cell: (info) => {
													const trackInventory =
														info?.row?.original?.trackInventory;
													const quantity = info?.row?.original?.quantity;
													const min = info?.row?.original?.minimum;
													const max = info?.row?.original?.maximum;

													return trackInventory
														? getStock({ quantity, min, max })?.badge
														: '-';
												},
											},
										]}
										data={{ rows: storagePointConsumables || [] }}
										hasCheckboxes={false}
										usePagination={false}
										manualSorting={false}
									/>
								);
							}
							if (tab === 'configure') {
								return (
									<>
										<div className="grid grid-cols-12 gap-4 gap-x-8 pb-6">
											<div className="col-span-12">
												<div className="bg-gray-100 p-6 my-6">
													<ul role="list" className="space-y-3">
														{shallowStoragePoint?.shelves
															?.sort(
																(a, b) =>
																	(storagePoint?.shelfOrder || []).indexOf(
																		a.id
																	) >
																	(storagePoint?.shelfOrder || []).indexOf(b.id)
															)
															?.map((shelf, shelfIdx) => {
																return (
																	<li
																		key={shelfIdx}
																		className="rounded-md bg-white px-6 py-4 "
																	>
																		<div className="mb-3 border-b pb-3 flex justify-between">
																			<div>
																				{editingTitle === shelfIdx ? (
																					<div className="flex items-center">
																						<TemplateField
																							placeholder="Enter new shelf name"
																							autoFocus
																							onChange={(index, title) => {}}
																							className="-mt-3 w-30 mr-3"
																							onEnter={(e) =>
																								handleTitleUpdate(
																									e.target.value,
																									shelf?.id
																								)
																							}
																							onBlur={(value) =>
																								handleTitleUpdate(
																									value,
																									shelf?.id
																								)
																							}
																							defaultValue={shelf?.name}
																							disabled={
																								isUpdatingShelf === 'pending'
																							}
																						/>
																						{isUpdatingShelf === 'pending' && (
																							<Spinner />
																						)}
																					</div>
																				) : (
																					<Label
																						onClick={() =>
																							setEditingTitle(shelfIdx)
																						}
																					>
																						<span className="cursor-text	">
																							{shelf?.name ||
																								`Shelf ${shelfIdx + 1}`}
																						</span>
																					</Label>
																				)}
																			</div>
																			{/*<div className="text-xxs  flex items-center">
																				{shelfIdx !== 0 && (
																					<div
																						className="cursor-pointer  flex items-center"
																						onClick={() =>
																							handleShiftShelf(shelfIdx, 'up')
																						}
																					>
																						<ArrowUpIcon className="w-3 h-3 text-gray-400 mr-1.5" />
																						Move Up
																					</div>
																				)}
																				{shelfIdx !==
																					configuration?.length - 1 && (
																					<div
																						className="cursor-pointer  flex items-center"
																						onClick={() =>
																							handleShiftShelf(shelfIdx, 'down')
																						}
																					>
																						<ArrowDownIcon className="w-3 h-3 text-gray-500 mr-1.5 ml-3" />
																						Move Down
																					</div>
																				)}
																					</div>*/}
																		</div>
																		<div className="grid grid-cols-5 gap-x-6 gap-y-4">
																			{shelf?.inventories?.length
																				? shelf?.inventories?.map((card, i) =>
																						renderCard(card, i, shelfIdx)
																				  )
																				: null}
																			<div
																				className="border border-dashed rounded-xl flex justify-center items-center flex-col cursor-pointer p-8 group hover:border-gray-400 h-64 min-h-full"
																				onClick={() =>
																					setAddConsumableOpen({
																						row: shelfIdx,
																						id: shelf?.id,
																					})
																				}
																			>
																				<div className="flex flex-1 flex-col items-center justify-center">
																					<PlusIcon className="w-5 h-5 text-gray-300" />
																					<div className="mt-3 text-gray-600 group-hover:text-gray-800 text-xs text-center">
																						Add a Consumable
																					</div>
																				</div>
																			</div>
																		</div>
																	</li>
																);
															})}
													</ul>
													<div className="relative">
														<div
															className="absolute inset-0 flex items-center"
															aria-hidden="true"
														>
															<div className="w-full border-t border-gray-300" />
														</div>
														<div className="relative flex justify-center mt-4">
															<button
																type="button"
																className="inline-flex items-center gap-x-1.5 rounded-full bg-white px-3 py-1.5 text-xxs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
																onClick={() =>
																	onAddShelf(null, () =>
																		onRefreshStoragePoint()
																	)
																}
															>
																<PlusIcon
																	className="-ml-1 -mr-0.5 h-5 w-5 text-gray-400"
																	aria-hidden="true"
																/>
																Add Shelf
															</button>
														</div>
													</div>
												</div>
											</div>
										</div>
									</>
								);
							}

							if (tab === 'settings') {
								return (
									<div className="space-y-12 mt-8">
										<div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
											<div>
												<h2 className="text-sm font-semibold leading-7 text-gray-900">
													General Information
												</h2>
												<p className="mt-1 text-xs leading-6 text-gray-600">
													Basic information about this storage point
												</p>
											</div>
											<div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8">
												<TemplateField name="Name" />
											</div>
										</div>

										<div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
											<div>
												<h2 className="text-sm font-semibold leading-7 text-gray-900">
													Delete Storage Point
												</h2>
												<p className="mt-1 text-xs leading-6 text-gray-600">
													Once you delete a storage point, there is no going
													back. Please be certain
												</p>
											</div>
											<div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8">
												<div>
													<Button
														type="danger"
														onClick={() =>
															openDeleteModal({
																title:
																	'Are you sure you want to delete this storage point?',
																form: (form, onChange) => (
																	<div className="py-4">
																		<TemplateBoolean
																			name="Reset inventory counts (if applicable)?"
																			onChange={(index, reset) => {
																				if (typeof onChange === 'function') {
																					onChange({
																						...form,
																						resetInventory: reset,
																					});
																				}
																			}}
																			value={form?.resetInventory}
																		/>
																		<p className="text-xxs mt-2">
																			By resetting the inventory count, you
																			remove all consumables attached to this
																			storage point
																		</p>
																	</div>
																),
																defaultForm: { resetInventory: true },
																confirmText: 'Delete Storage Point',
																onConfirm: (form) =>
																	onDeleteStoragePoint(
																		{
																			id,
																			resetInventory: form?.resetInventory,
																		},
																		() => {
																			closeDeleteModal();
																			navigate(
																				'/consumables/storage-points'
																			);
																		}
																	),
															})
														}
													>
														Delete Storage Point
													</Button>
												</div>
											</div>
										</div>
									</div>
								);
							}
						}}
					</Tabs>

					<div classNames="my-6">
						<div className="grid grid-cols-4"></div>
					</div>

					<Modal
						title="Add Consumable"
						open={isAddConsumableOpen}
						onClose={() => setAddConsumableOpen(null)}
					>
						<div className="space-y-4 grid grid-cols-2 gap-x-4">
							<BetaAttributes
								field={{
									label: 'Consumable',
									type: 'consumable',
									key: 'consumable',
									moduleId: CONSUMABLES_MODULE_ID,
									modal: {
										title: 'Quick Add Consumable',
									},
									isRequired: true,
								}}
								form={addConsumableForm}
								onChange={(index, value) => {
									if (value) {
										setAddConsumableForm({
											...addConsumableForm,
											[index]: { value },
										});
									} else {
										let updatedForm = { ...addConsumableForm };
										delete updatedForm[index];
										setAddConsumableForm(updatedForm);
									}
								}}
							/>

							<BetaAttributes
								field={{
									label: 'Miniumum Threshold',
									type: 'numeric',
									key: 'minimum',
									span: 1,
								}}
								form={addConsumableForm}
								onChange={(index, value) => {
									setAddConsumableForm({
										...addConsumableForm,
										[index]: { value },
									});
								}}
							/>

							<BetaAttributes
								field={{
									label: 'Maximum Threshold',
									type: 'numeric',
									key: 'maximum',
									span: 1,
								}}
								form={addConsumableForm}
								onChange={(index, value) => {
									setAddConsumableForm({
										...addConsumableForm,
										[index]: { value },
									});
								}}
							/>

							<TemplateBoolean
								name="Track Inventory"
								className="pb-2"
								value={addConsumableForm?.trackInventory}
								dataIndex="trackInventory"
								onChange={(index, value) => {
									if (value) {
										setAddConsumableForm({
											...addConsumableForm,
											[index]: { value },
										});
									} else {
										let updatedForm = { ...addConsumableForm };
										delete updatedForm[index];
										setAddConsumableForm(updatedForm);
									}
								}}
								span="sm:col-span-2"
							/>

							{addConsumableForm?.trackInventory?.value ? (
								<>
									<BetaAttributes
										field={{
											label: 'Quantity',
											type: 'numeric',
											key: 'quantity',
										}}
										form={addConsumableForm}
										onChange={(index, value) => {
											setAddConsumableForm({
												...addConsumableForm,
												[index]: { value },
											});
										}}
									/>
								</>
							) : null}

							<div className="flex justify-end mt-4 col-span-2">
								<Button
									disabled={!useRequired(addConsumableForm, ['consumable'])}
									onClick={() => {
										onAddConsumable(
											{
												id,
												consumableId:
													addConsumableForm?.consumable?.value?.consumable?.id,
												shelfId: isAddConsumableOpen?.id,
												trackInventory:
													addConsumableForm?.trackInventory?.value,
												quantity: addConsumableForm?.quantity?.value,
												minimum: addConsumableForm?.minimum?.value,
												maximum: addConsumableForm?.maximum?.value,
											},
											(response) => {
												let updatedConfig = [...configuration];

												updatedConfig[
													isAddConsumableOpen?.row || 0
												]?.rows?.push(response);

												setConfiguration(updatedConfig);
												setAddConsumableOpen(null);
											}
										);
									}}
									//disabled={useRequired(selectedConsumable, 'id')}
								>
									{isAddingConsumable === 'pending'
										? 'Adding consumable'
										: 'Add consumable'}
								</Button>
							</div>
						</div>
					</Modal>
				</>
			)}
		</MainContent>
	);
};

export default StoragePoint;
